import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment-timezone';
import { ChargesService } from '../../../../services/charges/charges.service';
import { CheckoutService } from '../../../../services/billing/checkout.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { ScrollService } from '../../../../services/scroll.service';
import { StripeService } from '../../../../services/billing/stripe.service';
import { ModalService } from '../../../../services/modal.service';
import { LoadingModalComponent } from '../../shared/loading-modal/loading-modal.component';
import { Cart } from '../../../../services/billing/cart.model';
import { CartItem } from '../../../../services/billing/cart-item.model';
import { Charge } from '../../../../models/entities/charge';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { PaymentMethod } from '../../../../services/billing/payment-method.model';
import { PaymentMethodSelectorComponent } from '../../shared/payment-method-selector/payment-method-selector.component';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { ValidationMessageService } from '../../../../services/validation-message.service';
import { PaymentGroup } from '../../../../models/entities/payment-group';
import { Store } from '@ngrx/store';
import { SetPaymentAction } from '../../../../store/actions/payment.actions';
import * as fromRoot from '../../../../store/reducers/app.reducer';
import { MembersService } from '../../../../services/members/members.service';
import { Organization } from '../../../../models/entities/organization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GrowlerService } from 'app/services/growler.service';

@UntilDestroy()
@Component({
    selector: 'lf-make-payment',
    styleUrls: ['./make-payment.scss'],
    templateUrl: './make-payment.component.html',
})
export class MakePaymentComponent implements OnInit
{
    jwt: JwtLegFiClaims;
    organization: Organization = null;
    accountBalance: number = null; // User's total currently due balance.
    overdueCount: number = null; // Number of charges currently overdue.
    paymentsSubtotal: number = 0; // Subtotal of selected payments.
    paymentsTotal: number = 0; // Payments total with processing fee.
    userAccountRoute: string = '/app/owner/dashboard';

    isGuardingDoubleSubmission = false;
    charges: Charge[] = [];
    selectedCharges: Charge[] = [];
    hasChargesSelected = false;
    isLoadingCharges = true;
    hasAutopayConfiguration = false;
    hasAutoPay = false;
    doShowLoadingModal = false;
    isPaymentMethodNotSelected = true;

    paymentMethod: PaymentMethod = null;

    @ViewChild('paymentMethodSelector', {read: PaymentMethodSelectorComponent})
    paymentMethodSelector: PaymentMethodSelectorComponent;

    loadingModalComponent = LoadingModalComponent;
    moment = moment;
    hasOrgCharges = false;
    doPayFeesFirst = false;
    hasLateFees = false;


    constructor(
            private _activatedRoute: ActivatedRoute,
            private _changeDetectorRef: ChangeDetectorRef,
            private _chargesService: ChargesService,
            private _checkoutService: CheckoutService,
            private _growlerService: GrowlerService,
            private _membersService: MembersService,
            private _modalService: ModalService,
            private _organizationsService: OrganizationService,
            private _router: Router,
            private _scrollService: ScrollService,
            private _stripeService: StripeService,
            private _validationMessageService: ValidationMessageService,
            private _store: Store<fromRoot.State>,
    ) {

    }

    get isButtonDisabled(): boolean {
        return this.isGuardingDoubleSubmission || !this.hasChargesSelected || !this.paymentMethod
                || this.paymentMethod.value === 'new-bank-account' || this.paymentMethod.value === 'new-credit-card';
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        if (this.jwt.unitId) {
            this.userAccountRoute = '/app/unit/detail/' + this.jwt.unitId;
        }

        this._activatedRoute.params.pipe(untilDestroyed(this)).subscribe(
                params => {
                    this.hasOrgCharges = !!params['oc'] || false;

                    const orgId = (this.hasOrgCharges) ? this.jwt.parentId : this.jwt.orgId;
                    this._organizationsService.getOrganizationById(orgId, [
                        'rates',
                        'preferences',
                    ]).subscribe(
                            (organization: Organization) => {
                                this.organization = organization;
                                this.doPayFeesFirst = organization.preferences.paymentsPayFeesFirst;
                                if (!this.hasOrgCharges) {
                                    this.getUserCharges();
                                } else {
                                    this.getOrgCharges();
                                }
                            },
                            () => {
                                this._growlerService.error('Error', 'Unable to load your organization\'s settings.');
                            },
                    );
                },
        );
        // Scroll to top on view load.
        this._scrollService.scrollToTop();
    }

    getOrgCharges(): void {
        this.isLoadingCharges = true;
        this._chargesService.getReceivedCharges(null).pipe(untilDestroyed(this)).subscribe(
                (response) => {
                    let isChecked = false;
                    const charges = ChargesService.castDatesToMoment(response);
                    let sum: number = 0;
                    charges.forEach((charge: Charge) => {
                        if (charge.status !== 'future') {
                            sum += charge.balance;
                            charge.paymentAmount = charge.balance;
                            charge.checked = true;
                            isChecked = true;
                        } else {
                            charge.paymentAmount = 0;
                            charge.checked = false;
                        }
                    });
                    this.accountBalance = sum;

                    charges.sort((a: Charge, b: Charge) => {
                        if (a.paymentDueOn.isBefore(b.paymentDueOn)) {
                            return -1;
                        }
                        if (a.paymentDueOn.isAfter(b.paymentDueOn)) {
                            return 1;
                        }
                        return 0;
                    });
                    this.charges = charges;
                    this.overdueCount = charges.filter((charge) => {
                        return charge && charge.status && charge.status === 'overdue';
                    }).length;
                    this.calculatePaymentsTotal();
                    this.isLoadingCharges = false;
                    this.updateSelectedCharges();

                },
                () => {
                    this.hasChargesSelected = false;
                    this.isLoadingCharges = false;
                },
        );
    }

    feeParentIsPresent(fee: Charge) {
        if (!fee.parentId) {
            return false;
        }
        for (let i = 0; i < this.charges.length; i++) {
            if (this.charges[i].id === fee.parentId) {
                return true;
            }
        }
        return false;
    }

    getUserCharges(): void {
        this.isLoadingCharges = true;
        this._chargesService.getUserCharges(null, 'current').pipe(untilDestroyed(this)).subscribe(
                (response) => {
                    let isChecked = false;
                    const charges = ChargesService.castDatesToMoment(response);
                    let sum: number = 0;
                    charges.forEach((charge: Charge) => {
                        if (charge.status !== 'future') {
                            sum += charge.balance;
                            charge.paymentAmount = charge.balance;
                            charge.checked = true;
                            isChecked = true;
                        } else {
                            charge.paymentAmount = 0;
                            charge.checked = false;
                        }
                        if (charge.lateFees.length) {
                            this.hasLateFees = true;
                        }
                    });
                    this.accountBalance = sum;

                    charges.sort((a: Charge, b: Charge) => {
                        let dateSort = 0;
                        if (a.paymentDueOn.isBefore(b.paymentDueOn)) {
                            dateSort = -1;
                        }
                        if (a.paymentDueOn.isAfter(b.paymentDueOn)) {
                            dateSort = 1;
                        }
                        return dateSort;
                    });

                    const parentCharges = [];
                    const childCharges = [];
                    charges.forEach(charge => {
                        if (!charge.parentId) {
                            parentCharges.push(charge);
                        } else {
                            let isParentInList = false;
                            charges.forEach(c => {
                                if (c.id === charge.parentId) {
                                    isParentInList = true;
                                }
                            });
                            if (isParentInList) {
                                childCharges.push(charge);
                            } else {
                                parentCharges.push(charge);
                            }
                        }
                    });

                    childCharges.forEach(charge => {
                        for (let i = 0; i < parentCharges.length; i++) {
                            if (parentCharges[i].id === charge.parentId) {
                                parentCharges.splice(i + 1, 0, charge);
                                return;
                            }
                        }
                    });

                    this.charges = parentCharges;
                    this.overdueCount = charges.filter((charge) => {
                        return charge && charge.status && charge.status === 'overdue';
                    }).length;

                    this.calculatePaymentsTotal();
                    this.isLoadingCharges = false;
                    this.updateSelectedCharges();

                },
                () => {
                    this.hasChargesSelected = false;
                    this.isLoadingCharges = false;
                },
        );

        this._membersService.getMemberSummary(this.jwt.orgId, null).pipe(untilDestroyed(this)).subscribe(
                (user: any) => {
                    this.hasAutoPay = user.membership.profile.autopayEnabled;
                },
                () => {
                    console.error('Unable to load member summary.');
                },
        );
    }

    //noinspection JSMethodCanBeStatic
    modifyRowCheckedStatus(charge: Charge, isChecked: boolean) {
        const isFee = !!charge.parentId;
        const isFullPayment = charge.paymentAmount === charge.balance;

        if (isFee) {
            if (this.doPayFeesFirst) {
                if (!isChecked || !isFullPayment) {
                    // uncheck the parent
                    this.charges.forEach(c => {
                        if (c.id === charge.parentId) {
                            c.checked = false;
                            c.paymentAmount = 0;
                        }
                    });
                }
            }
        } else {
            if (this.doPayFeesFirst) {
                if (isChecked) {
                    // check all children
                    this.charges.forEach(c => {
                        if (c.parentId === charge.id) {
                            c.checked = true;
                            c.paymentAmount = c.balance;
                        }
                    });
                }
            }
        }

        charge.checked = isChecked;

        if (isChecked && !charge.paymentAmount) {
            charge.paymentAmount = charge.balance;
        } else if (!isChecked) {
            charge.paymentAmount = 0;
        }
        this.updateSelectedCharges();
    }

    selectCharges(checked: boolean): void {
        if (checked) {
            this.checkAllCharges();
        } else {
            this.uncheckAllCharges();
        }
    }

    checkAllCharges(): void {
        let isChecked = false;
        this.charges.map((charge: any) => {
            isChecked = true;
            charge.checked = true;
            charge.paymentAmount = charge.balance;
        });
        this.hasChargesSelected = isChecked;
        this.updateSelectedCharges();
        this.calculatePaymentsTotal();
    }

    uncheckAllCharges(): void {
        this.charges.map((charge: any) => {
            charge.checked = false;
            charge.paymentAmount = 0;
        });
        this.hasChargesSelected = false;
        this.updateSelectedCharges();
        this.calculatePaymentsTotal();
    }

    updateSelectedCharges(): void {
        this.selectedCharges = this.charges.filter((charge) => {
            return charge.checked;
        });
        this.calculatePaymentsTotal();
    }

    /** User clicked checkout */
    checkout() {
        if (this.isReadyForCheckout()) {
            this.doShowLoadingModal = true;
            this.isGuardingDoubleSubmission = true;

            // Create a cart full of the selected charges as cart items.
            const cart = <Cart>{};
            cart.items = [];
            this.selectedCharges.forEach((charge) => {
                cart.items.push(<CartItem>{
                    organizationId: charge.organizationId,
                    id: charge.id,
                    type: 'charge',
                    title: charge.title,
                    depositBankAccountId: charge.depositBankAccountId,
                    quantity: 1,
                    paymentAmount: charge.paymentAmount,
                    currency: charge.currency,
                    size: null,
                });
            });
            cart.paymentMethod = <PaymentMethod>{type: '', value: ''};

            // Make sure we provide the correct payment method information.
            if (this.paymentMethod &&
                    [
                        'CreditCard',
                        'CardToken',
                        'DepositBankAccount',
                        'PaymentBankAccount',
                        'OfflineCheck',
                        'OfflineCash',
                        'AccountCredit',
                        'Mailed',
                    ].indexOf(
                            this.paymentMethod.type,
                    ) !== -1
            ) {
                cart.paymentMethod.type = this.paymentMethod.type;
                cart.paymentMethod.value = this.paymentMethod.value && this.paymentMethod.value.id
                        ? this.paymentMethod.value.id
                        : null;
            } else {
                this._growlerService.error('Error', 'Unable to process your checkout.');
                this.isGuardingDoubleSubmission = false;
                this.doShowLoadingModal = false;
                return;
            }

            // Hit appropriate checkout endpoint (anonymous vs member).
            const request = this.hasOrgCharges
                    ? this._checkoutService.organizationCheckout(null, cart)
                    : this._checkoutService.memberCheckout(null, cart);

            // Process the checkout.
            request.subscribe(
                    (response) => {
                        const paymentGroups: PaymentGroup[] = (<PaymentGroup[]>response);

                        // Put payment into store; TODO - eliminate the need for this
                        this._store.dispatch(new SetPaymentAction(paymentGroups));

                        // noinspection JSIgnoredPromiseFromCall
                        this._router.navigate([
                            '/app/receipt',
                            {
                                paymentGroups: paymentGroups.map((payment) => {
                                    return payment.id;
                                }),
                            },
                        ]);
                    },
                    (response) => {
                        const message = this._validationMessageService.extractResponseErrorText(response)
                                || 'Failed processing payments.';
                        this._growlerService.error('Error', message);
                        this.isGuardingDoubleSubmission = false;
                        this.doShowLoadingModal = false;
                    },
            );
        }
    }

    /** These states shouldn't be possible b/c button will be disabled but... check anyway */
    isReadyForCheckout(): boolean {
        this.isGuardingDoubleSubmission = true;
        this.doShowLoadingModal = true;

        const errors: string[] = [];

        // Verify at least 1 charge is selected.
        if (!this.hasChargesSelected) {
            errors.push('You must select some charges to pay.');
        }

        // Verify a payment method is selected.
        if (!this.paymentMethod || this.paymentMethod.value === 'new-bank-account' || this.paymentMethod.value === 'new-credit-card') {
            errors.push('You must select a payment method.');
            this.isPaymentMethodNotSelected = true;
        }

        if (errors.length) {
            const message = errors.join('\r\n');
            this._growlerService.error('Error', message);
            this.isGuardingDoubleSubmission = false;
            this.doShowLoadingModal = false;

            return false;
        }

        return true;
    }

    calculatePaymentsTotal(): void {
        let isChecked = false;
        let sum: number = 0;
        this.charges.map((charge) => {
            isChecked = isChecked || charge.checked;
            if (charge.checked) {
                sum += Number(charge.paymentAmount);
            }
        });
        this.hasChargesSelected = isChecked;
        this.paymentsSubtotal = sum;
        this.paymentsTotal = this.paymentsSubtotal;
        if (this.paymentMethod) {
            if ((this.paymentMethod.value === 'new-credit-card' || this.paymentMethod.value.cardBrand) && !this.organization.profile.absorbsCcFees) {
                this.paymentsTotal += Math.round(this.paymentsTotal * (Number(this.organization.rates.merchantRate) / 100))
                        + Math.round(this.organization.rates.merchantMinimum * 100);
            } else if (this.paymentMethod.value.routingNumber && !this.organization.profile.absorbsAchFees) {
                this.paymentsTotal += Math.round(this.paymentsTotal * (Number(this.organization.rates.checkRate) / 100))
                        + Math.round(this.organization.rates.checkRateFlat * 100);
            }
        }
    }

    updateAutopayStatus() {
        this.hasAutoPay = !this.hasAutoPay;
        if (this.hasAutoPay) {
            this.paymentMethodSelector.getPaymentMethods();
        }
    }

    paymentMethodChanged($event: PaymentMethod) {
        this.isPaymentMethodNotSelected = false;
        this.paymentMethod = $event;
        this.calculatePaymentsTotal();
    }
}
